import React from 'react';
import { motion } from 'framer-motion';
import { Check, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

const packageImages = {
  create: 'https://static.prod-images.emergentagent.com/jobs/d2c49928-1757-4269-abb0-4f6580d9c2e4/images/da6a58e341ef0164a32050380c79740f873a57cb99ceab5e7238cf0dd304c6e8.png',
  celebrate: 'https://static.prod-images.emergentagent.com/jobs/d2c49928-1757-4269-abb0-4f6580d9c2e4/images/7abbb0cbfd941a4d84ce25c8f6c60b4d5177940b780bb6ade49fc446824b859c.png',
  signature: 'https://static.prod-images.emergentagent.com/jobs/d2c49928-1757-4269-abb0-4f6580d9c2e4/images/e32a284509ea90571435a4b5688d69a2811bd9bd375c0a18ed0fe4ba0cc530cd.png'
};

export const PackageCard = ({ pkg, isSelected, onSelect, index, offer = null }) => {
  const colors = {
    create: { bg: '#FFE4EC', accent: '#FF3B6B' },
    celebrate: { bg: '#E0FFF5', accent: '#06D6A0' },
    signature: { bg: '#FFF4D6', accent: '#FFD166' }
  };

  const color = colors[pkg.id] || colors.create;

  // Compute discounted price for display when an active offer applies.
  const offerLabel = offer
    ? (offer.discount_type === 'percent' ? `${offer.discount_value}% OFF` : `$${Number(offer.discount_value).toFixed(0)} OFF`)
    : null;
  const discountedPrice = offer
    ? (offer.discount_type === 'percent'
        ? Math.max(0, Math.round((pkg.price - (pkg.price * offer.discount_value / 100)) * 100) / 100)
        : Math.max(0, pkg.price - Number(offer.discount_value)))
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className={`relative rounded-2xl overflow-hidden cursor-pointer card-hover border-2 transition-all duration-300 ${
        isSelected 
          ? 'border-[#FF3B6B] shadow-xl ring-2 ring-[#FF3B6B]/20' 
          : 'border-[#E8E5DF] hover:border-[#FF3B6B]/50'
      }`}
      onClick={onSelect}
      data-testid={`package-card-${pkg.id}`}
    >
      {/* Special offer badge — top-left corner with rotated ribbon look. */}
      {offer && (
        <div
          className="absolute top-3 left-3 z-10 flex items-center gap-1 rounded-full bg-[#FF3B6B] text-white px-3 py-1 text-[11px] font-bold shadow-lg"
          data-testid={`package-offer-badge-${pkg.id}`}
          title={offer.title}
        >
          <Sparkles className="w-3 h-3" />
          {offerLabel}
        </div>
      )}
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img 
          src={packageImages[pkg.id]} 
          alt={pkg.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 package-card-overlay" />
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-xl font-bold text-white" style={{ fontFamily: 'Fredoka, sans-serif' }}>
            {pkg.name}
          </h3>
          <p className="text-white/90 text-sm">{pkg.duration}</p>
        </div>
        {isSelected && (
          <div className="absolute top-4 right-4 w-8 h-8 bg-[#FF3B6B] rounded-full flex items-center justify-center">
            <Check className="w-5 h-5 text-white" />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-6 bg-white">
        <div className="flex items-baseline justify-between mb-4">
          {offer ? (
            <span className="flex items-baseline gap-2" data-testid={`package-discounted-price-${pkg.id}`}>
              <span className="text-3xl font-bold text-[#FF3B6B]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                ${discountedPrice}
              </span>
              <span className="text-base text-[#6B6B7A] line-through">${pkg.price}</span>
            </span>
          ) : (
            <span className="text-3xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              ${pkg.price}
            </span>
          )}
          <span className="text-sm text-[#6B6B7A]">Up to {pkg.guests} guests</span>
        </div>

        {offer && (
          <p className="mb-3 text-[11px] text-[#FF3B6B] font-semibold uppercase tracking-wide">
            {offer.title} — applied automatically
          </p>
        )}

        <ul className="space-y-2 mb-6">
          {pkg.features.slice(0, 5).map((feature, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-[#6B6B7A]">
              <Check className="w-4 h-4 text-[#06D6A0] flex-shrink-0 mt-0.5" />
              <span>{feature}</span>
            </li>
          ))}
          {pkg.features.length > 5 && (
            <li className="text-sm text-[#6B6B7A] pl-6">
              +{pkg.features.length - 5} more features
            </li>
          )}
        </ul>

        <Button 
          className={`w-full rounded-full btn-hover ${
            isSelected 
              ? 'bg-[#FF3B6B] hover:bg-[#E62E58] text-white' 
              : 'bg-white border-2 border-[#FF3B6B] text-[#FF3B6B] hover:bg-[#FF3B6B] hover:text-white'
          }`}
          data-testid={`package-select-btn-${pkg.id}`}
        >
          {isSelected ? 'Selected' : 'Select Package'}
        </Button>
      </div>
    </motion.div>
  );
};

export default PackageCard;
